import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Panel } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { TenanciesNav } from '../features/arrears/TenanciesNav';
import { RentReviewRegister } from '../features/rentReviews/RentReviewRegister';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/tenancies/rent-reviews')({
  component: RentReviewsPage,
});

type Filter = 'work' | 'all' | 'everyone';

/**
 * Rent reviews — whose rent can go up, and whether each increase has its notice.
 *
 * UNDER `/tenancies`, beside Arrears and for its reason: every row is a letting and every action
 * lands on one. The management dashboard's card says a review is coming; this is where it is done.
 *
 * THE DEFAULT FILTER IS THE WORK. "All" adds the increases already noticed and the reviews still
 * too early to serve, which is how somebody checks what is in train; "Every tenancy" adds the ones
 * with nothing due, which is how they check one has not fallen off the query.
 */
function RentReviewsPage() {
  const [filter, setFilter] = useState<Filter>('work');

  const register = useQuery(
    trpc.rentReviews.register.queryOptions(
      {
        ...(filter === 'work' ? { outstandingOnly: true } : {}),
        ...(filter === 'everyone' ? { includeNotDue: true } : {}),
      },
      { staleTime: 5_000 },
    ),
  );

  const summary = register.data?.summary;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="Rent reviews" subtitle="Whose rent can go up, and the notice behind it" />
      <TenanciesNav />

      <div className="flex flex-col gap-4">
        {summary ? (
          <Panel className="min-w-0 p-0">
            <div className="grid grid-cols-2 gap-px bg-rule sm:grid-cols-5">
              {/* `not_due` is most of a portfolio most of the year, and is not a column for the
                  reason `current` is not one on the arrears ladder. Empty states are shown. */}
              {summary.states
                .filter((entry) => entry.state !== 'not_due')
                .map((entry) => (
                  <div key={entry.state} className="bg-surface px-4 py-3" title={entry.hint}>
                    <p className="text-[0.6875rem] tracking-wide text-ink-muted uppercase">
                      {entry.label}
                    </p>
                    <p className="mt-1 text-[0.9375rem] font-semibold tabular-nums">
                      {entry.count === 0 ? '—' : String(entry.count)}
                    </p>
                  </div>
                ))}
            </div>
          </Panel>
        ) : null}

        <RentReviewRegister
          register={register.data}
          isPending={register.isPending}
          filters={
            <>
              <FilterButton current={filter} value="work" onPick={setFilter}>
                Waiting on us
              </FilterButton>
              <FilterButton current={filter} value="all" onPick={setFilter}>
                All in train
              </FilterButton>
              <FilterButton current={filter} value="everyone" onPick={setFilter}>
                Every tenancy
              </FilterButton>
            </>
          }
        />
      </div>
    </div>
  );
}

function FilterButton({
  current,
  value,
  onPick,
  children,
}: {
  current: Filter;
  value: Filter;
  onPick: (filter: Filter) => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={current === value ? 'primary' : 'secondary'}
      onClick={() => onPick(value)}
    >
      {children}
    </Button>
  );
}
